import { Controller } from "@hotwired/stimulus"

// Collapses the Academy top bar search into an icon button (TMS-929). The
// first click on the icon expands the field instead of submitting; once
// expanded, the same button behaves as a normal submit button.
export default class extends Controller {
  static targets = ["input"]

  connect() {
    this._handleOutsideClick = this._handleOutsideClick.bind(this)
    this._handleKeydown = this._handleKeydown.bind(this)
    document.addEventListener("click", this._handleOutsideClick)
    document.addEventListener("keydown", this._handleKeydown)

    // Auto-expanding for a pre-filled query (landing on the results page)
    // must not steal focus from the page the user actually came to read.
    if (this.inputTarget.value) {
      this._expand({ focus: false })
    }

    // A static data-controller attribute is present in the server-rendered
    // HTML before Stimulus has actually connected -- a click that lands in
    // that window falls through to a real form submit instead of expanding
    // the field. This class only appears once connect() has run, so specs
    // can wait on it instead of racing the click.
    this.element.classList.add("topbar-search--connected")
  }

  disconnect() {
    document.removeEventListener("click", this._handleOutsideClick)
    document.removeEventListener("keydown", this._handleKeydown)
  }

  toggle(event) {
    if (this._isExpanded()) return

    event.preventDefault()
    this._expand()
  }

  // Private

  _expand({ focus = true } = {}) {
    this.element.classList.add("is-expanded")
    if (focus) this.inputTarget.focus()
  }

  _collapse() {
    if (this.inputTarget.value) return

    this.element.classList.remove("is-expanded")
  }

  _isExpanded() {
    return this.element.classList.contains("is-expanded")
  }

  _handleOutsideClick(event) {
    if (!this._isExpanded()) return
    if (this.element.contains(event.target)) return

    this._collapse()
  }

  _handleKeydown(event) {
    if (event.key === "Escape" && this._isExpanded()) {
      this._collapse()
      this.inputTarget.blur()
    }
  }
}
